import React, { useEffect, useState, memo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { CustomHeaderGroupProps } from 'ag-grid-react'
import { Button } from 'antd'
import { LeftOutlined, RightOutlined } from '@ant-design/icons'

/**
 * Custom header group component for month groups.
 * Shows cumulative toggle button instead of the default expand/collapse icon.
 * Optimized with React.memo to prevent unnecessary re-renders.
 */
const MonthGroupHeaderRenderer: React.FC<CustomHeaderGroupProps> = ({ columnGroup, setExpanded }) => {
  const { t } = useTranslation()
  const [isExpanded, setIsExpanded] = useState(false)

  const toggleExpand = useCallback(() => {
    const currentState = columnGroup.getProvidedColumnGroup().isExpanded()
    setExpanded(!currentState)
  }, [columnGroup, setExpanded])

  const syncExpandState = useCallback(() => {
    setIsExpanded(columnGroup.getProvidedColumnGroup().isExpanded())
  }, [columnGroup])

  useEffect(() => {
    const cGroup = columnGroup.getProvidedColumnGroup()
    cGroup.addEventListener('expandedChanged', syncExpandState)
    syncExpandState()

    return () => {
      cGroup.removeEventListener('expandedChanged', syncExpandState)
    }
  }, [columnGroup, syncExpandState])

  // Check if this group is expandable (has columns with columnGroupShow)
  const isExpandable = columnGroup.isExpandable()

  return (
    <div className="month-group-header-container">
      {isExpandable && (
        <Button
          type="text"
          color="primary"
          size="small"
          variant={isExpanded ? 'filled' : 'text'}
          iconPosition={isExpanded ? 'start' : 'end'}
          onClick={toggleExpand}
          icon={isExpanded ? <LeftOutlined /> : <RightOutlined />}
        >
          {t('financialStatementsPage:cumulative')}
        </Button>
      )}
    </div>
  )
}

export default memo(MonthGroupHeaderRenderer)
